import {
  Component,
  ChangeDetectionStrategy,
  input,
  output,
  computed,
  forwardRef,
  signal,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

export type TextareaSize = 'sm' | 'md' | 'lg';
export type TextareaResize = 'none' | 'vertical' | 'horizontal' | 'both';

@Component({
  selector: 'app-atom-textarea',
  standalone: true,
  imports: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TextareaComponent),
      multi: true,
    },
  ],
  templateUrl: './textarea.component.html',
  styleUrls: ['./textarea.component.scss'],
})
export class TextareaComponent implements ControlValueAccessor {
  // Signal-based inputs
  placeholder = input<string>('');
  disabledInput = input(false, { alias: 'disabled' });
  private _disabledFromCVA = signal<boolean>(false);
  disabled = computed(() => this.disabledInput() || this._disabledFromCVA());
  readonly = input<boolean>(false);
  required = input<boolean>(false);
  error = input<boolean>(false);
  hasError = input<boolean>(false);
  errorMessage = input<string>('');
  label = input<string>('');
  helperText = input<string>('');
  id = input<string>('');
  name = input<string>('');
  rows = input<number>(4);
  maxlength = input<number | null>(null);
  size = input<TextareaSize>('md');
  resize = input<TextareaResize>('vertical');
  showCharCount = input<boolean>(false);
  additionalClasses = input<string>('');

  // Signal-based outputs
  inputBlur = output<void>();
  inputFocus = output<void>();

  // Internal value signal for ControlValueAccessor
  internalValue = signal<string>('');

  // ControlValueAccessor callbacks
  private onChange: (value: string) => void = () => {};
  private onTouched: () => void = () => {};

  // Computed classes
  textareaClasses = computed(() => {
    const classes: string[] = [
      'w-full',
      'border-2',
      'rounded-lg',
      'transition-all',
      'duration-200',
      'focus:outline-none',
      'focus:ring-2',
      'focus:ring-offset-0',
    ];

    // Size classes
    const sizeMap: Record<TextareaSize, string> = {
      sm: 'px-3 py-1.5 text-sm',
      md: 'px-4 py-2.5 text-sm',
      lg: 'px-4 py-3 text-base',
    };
    classes.push(sizeMap[this.size()]);

    // Resize classes
    const resizeMap: Record<TextareaResize, string> = {
      none: 'resize-none',
      vertical: 'resize-y',
      horizontal: 'resize-x',
      both: 'resize',
    };
    classes.push(resizeMap[this.resize()]);

    // Error state
    if (this.error() || this.hasError()) {
      classes.push(
        'border-danger-500',
        'focus:ring-danger-500/20',
        'focus:border-danger-500',
        'bg-red-50/50'
      );
    } else {
      classes.push(
        'border-gray-200',
        'focus:ring-primary-500/20',
        'focus:border-primary-500',
        'bg-white',
        'hover:border-gray-300'
      );
    }

    // Disabled/readonly state
    if (this.disabled() || this.readonly()) {
      classes.push('bg-gray-100', 'cursor-not-allowed', 'opacity-60');
    }

    if (this.additionalClasses()) {
      classes.push(this.additionalClasses());
    }

    return classes.join(' ');
  });

  // ControlValueAccessor implementation
  writeValue(value: string): void {
    this.internalValue.set(value ?? '');
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this._disabledFromCVA.set(isDisabled);
  }

  // Event handlers
  onInputChange(event: Event): void {
    const target = event.target as HTMLTextAreaElement;
    const value = target.value;
    this.internalValue.set(value);
    this.onChange(value);
  }

  onBlur(): void {
    this.onTouched();
    this.inputBlur.emit();
  }

  onFocus(): void {
    this.inputFocus.emit();
  }
}
